import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, postJson } from "./lib/api";

export type User = {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  roles: string[];
};

type LoginResponse = { token: string; email: string; displayName: string; role: string; expiresAt: string };
type AuthState = {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  loginWithOtp: (otpCode: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("Token");
    if (!token) { setUser(null); setLoading(false); return; }
    try { setUser(await api<User>("/api/auth/me")); }
    catch { localStorage.removeItem("Token"); setUser(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const acceptLogin = async (response: LoginResponse) => {
    localStorage.setItem("Token", response.token);
    await refresh();
  };

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    refresh,
    loginWithToken: async (token) => acceptLogin(await postJson<LoginResponse>("/api/auth/validate-magic-link", { token })),
    loginWithOtp: async (otpCode) => acceptLogin(await postJson<LoginResponse>("/api/auth/validate-otp", { otpCode })),
    logout: () => { localStorage.removeItem("Token"); setUser(null); window.location.assign("/"); }
  }), [user, loading, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const state = useContext(AuthContext);
  if (!state) throw new Error("AuthProvider mancante");
  return state;
}
